import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Edit, Trash2, LogOut, Upload, X, Wand2, Image, Copy, Check, FolderOpen } from 'lucide-react';
import { 
  getAllPosts, createPost, updatePost, deletePost, 
  loginAdmin, logoutAdmin, checkAuth, getAllMedia, 
  uploadSiteImage, deleteMedia, updateSiteAsset, resetSiteAsset 
} from '../services/firebaseBlogService';
import WYSIWYGEditor from '../components/WYSIWYGEditor';
import AutoDesignModal from '../components/AutoDesignModal';
import { useSiteAssets } from '../context/SiteAssetsContext';

// Default static assets for previews
import heroImage from '../assets/hero/hero-main.jpg';
import companyImage from '../assets/factory/factory-exterior.png';
import connectorMfg from '../assets/manufacturing/connector-mfg.jpg';
import stampingServices from '../assets/manufacturing/stamping-services.jpg';
import moldDevelopment from '../assets/manufacturing/mold-development.jpg';
import injectionMolding from '../assets/manufacturing/injection-molding.jpg';
import cableAssembly from '../assets/products/cable-assembly.jpg';
import qualityControl from '../assets/factory/quality-control.jpg';

const Admin = ({ theme }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [posts, setPosts] = useState([]);
  const [showLoginForm, setShowLoginForm] = useState(true);
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    category: '',
    image: '',
    imageFile: null,
    // SEO fields
    seoTitle: '',
    seoDescription: '',
    canonicalUrl: ''
  });
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  // Auto Design Modal State
  const [showAutoDesignModal, setShowAutoDesignModal] = useState(false);
  const [autoDesignData, setAutoDesignData] = useState({
    title: '',
    mainImage: null,
    mainImagePreview: null,
    sections: [
      { heading: '', content: '', image: null, imagePreview: null },
      { heading: '', content: '', image: null, imagePreview: null },
      { heading: '', content: '', image: null, imagePreview: null }
    ]
  });

  // Media Manager State
  const [showMediaManager, setShowMediaManager] = useState(false);
  const [showPictureManager, setShowPictureManager] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState('');
  const [newImageDescription, setNewImageDescription] = useState('');
  const mediaFileInputRef = useRef(null);

  // Site Assets overrides hook
  const { assets, rawAssets, refreshAssets } = useSiteAssets();
  const [assetUploadLoading, setAssetUploadLoading] = useState({});
  const [assetError, setAssetError] = useState('');

  const siteAssetsList = [
    { key: 'home_hero', name: 'Home Page Hero Background', desc: 'Top header image on the home page.', defaultVal: heroImage },
    { key: 'home_company', name: 'Home Factory Exterior', desc: 'Main company profile image on the home page.', defaultVal: companyImage },
    { key: 'about_factory', name: 'About Page Overview', desc: 'Main factory overview image on the About page.', defaultVal: companyImage },
    { key: 'services_molding', name: 'Molding Workshop', desc: 'Showcase picture for Injection Molding services.', defaultVal: injectionMolding },
    { key: 'services_stamping', name: 'Stamping Workshop', desc: 'Showcase picture for Metal Stamping services.', defaultVal: stampingServices },
    { key: 'services_cnc', name: 'CNC Machining', desc: 'Showcase picture for Mold Development / CNC Machining.', defaultVal: moldDevelopment },
    { key: 'services_cable', name: 'Cable Assembly', desc: 'Showcase picture for Cable Assembly services.', defaultVal: cableAssembly },
    { key: 'services_conference', name: 'Conference / Quality Control', desc: 'Showcase picture for Quality Control section.', defaultVal: qualityControl },
  ];

  const handleAssetUpload = async (key, file) => {
    if (!file) return;
    
    if (!file.type.match('image.*')) {
      setAssetError('Please select a valid image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      setAssetError('File size exceeds 10MB limit');
      return;
    }
    
    setAssetUploadLoading(prev => ({ ...prev, [key]: true }));
    setAssetError('');
    
    try {
      await updateSiteAsset(key, file);
      await refreshAssets();
      alert('✨ Image updated successfully! Changes will reflect across the site.');
    } catch (err) {
      console.error('Error uploading asset:', err);
      setAssetError(`Failed to upload image: ${err.message || 'Please try again.'}`);
    } finally {
      setAssetUploadLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAssetReset = async (key, storagePath) => {
    if (window.confirm('Are you sure you want to reset this section to its default image?')) {
      setAssetUploadLoading(prev => ({ ...prev, [key]: true }));
      try {
        await resetSiteAsset(key, storagePath);
        await refreshAssets();
        alert('✓ Image reset to default successfully!');
      } catch (err) {
        console.error('Error resetting asset:', err);
        setAssetError('Failed to reset image to default');
      } finally {
        setAssetUploadLoading(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const authStatus = await checkAuth();
        setIsLoggedIn(authStatus.authenticated);
        if (authStatus.authenticated) {
          await loadPosts();
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setIsLoggedIn(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuthentication();
  }, []);

  const loadPosts = async () => {
    try {
      const postsData = await getAllPosts();
      setPosts(postsData);
    } catch (error) {
      console.error('Error loading posts:', error);
      setError('Failed to load blog posts');
    }
  };

  // Media Manager Functions
  const loadMedia = async () => {
    setMediaLoading(true);
    setMediaError('');
    try {
      const media = await getAllMedia();
      setMediaFiles(media);
    } catch (error) {
      console.error('Error loading media:', error);
      setMediaError('Failed to load media files');
    } finally {
      setMediaLoading(false);
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.match('image.*')) {
      setMediaError('Please select a valid image file (JPG, PNG, GIF, WebP)');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setMediaError('File size exceeds 10MB limit');
      return;
    }
    
    setUploadingFile(true);
    setMediaError('');
    
    try {
      await uploadSiteImage(file, newImageDescription);
      setNewImageDescription('');
      await loadMedia();
      if (mediaFileInputRef.current) {
        mediaFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      setMediaError(error.message || 'Failed to upload image');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteMedia = async (mediaId, storagePath) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await deleteMedia(mediaId, storagePath);
        await loadMedia();
      } catch (error) {
        console.error('Error deleting media:', error);
        setMediaError('Failed to delete image');
      }
    }
  };

  const copyImageUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(''), 2000);
  };

  const selectImageForPost = (url) => {
    setFormData(prev => ({
      ...prev,
      image: url,
      imageFile: null
    }));
    setPreviewImage(url);
    setSelectedImage(null);
    setShowMediaManager(false);
  };

  // Open media manager with existing images
  const openMediaManager = (mode = 'select') => {
    setSelectedImage(mode);
    loadMedia();
    setShowMediaManager(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const result = await loginAdmin(loginData.username, loginData.password);
      if (result.success) {
        setIsLoggedIn(true);
        setShowLoginForm(false);
        await loadPosts();
      } else {
        setError(result.error || 'Invalid credentials. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await logoutAdmin();
      setIsLoggedIn(false);
      setShowLoginForm(true);
      setLoginData({ username: '', password: '' });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setError('Please select a valid image file (JPG, PNG, GIF)');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB limit');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        image: '' // Clear URL if file is selected
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous errors
      setError('');
    }
  };

  // New function to handle drag and drop image upload
  const handleDropImage = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.match('image.*')) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB limit');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        image: ''
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
      
      setError('');
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData(prev => ({
      ...prev,
      image: url,
      imageFile: null // Clear file if URL is entered
    }));
    setPreviewImage(url);
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      imageFile: null,
      image: ''
    }));
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    // Validate required fields
    if (!formData.title || !formData.excerpt || !formData.content || !formData.author || !formData.category) {
      setError('Please fill in all required fields (Title, Excerpt, Content, Author, Category)');
      return;
    }
    
    setSaveLoading(true);
    
    try {
      let result;
      if (editingPost) {
        result = await updatePost(editingPost.id, formData);
        setSuccessMessage('✅ Post updated successfully!');
      } else {
        result = await createPost(formData);
        setSuccessMessage('✅ Post published successfully!');
      }
      
      console.log('Post saved:', result);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
      resetForm();
      await loadPosts();
    } catch (err) {
      console.error('Error saving post:', err);
      setError(`Error saving post: ${err.message || 'Please try again.'}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      category: post.category,
      image: post.image || '',
      imageFile: null,
      // SEO fields
      seoTitle: post.seoTitle || '',
      seoDescription: post.seoDescription || '',
      canonicalUrl: post.canonicalUrl || ''
    });
    setPreviewImage(post.image || null);
    setShowPostForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deletePost(id);
        await loadPosts();
      } catch (err) {
        console.error('Error deleting post:', err);
        setError('Failed to delete post');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: '',
      category: '',
      image: '',
      imageFile: null,
      // SEO fields
      seoTitle: '',
      seoDescription: '',
      canonicalUrl: ''
    });
    setPreviewImage(null);
    setEditingPost(null);
    setShowPostForm(false);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // New function to toggle preview mode
  const [showPreview, setShowPreview] = useState(false);

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  // Kimi AI Assist Feature
  const [showAIAssist, setShowAIAssist] = useState(false);
  const [aiAssistState, setAIAssistState] = useState({
    topic: '',
    category: 'General',
    isGenerating: false,
    error: null,
    suggestions: [],
    generatedContent: null
  });

  // Generate blog ideas from Kimi AI
  const generateIdeas = async () => {
    setAIAssistState(prev => ({ ...prev, isGenerating: true, error: null }));
    try {
      const { generateBlogIdeas } = await import('../services/kimiAIService');
      const result = await generateBlogIdeas(aiAssistState.category, 5);
      if (result.success) {
        const ideas = result.content.split('\n').filter(line => line.trim());
        setAIAssistState(prev => ({ ...prev, suggestions: ideas, isGenerating: false }));
      } else {
        setAIAssistState(prev => ({ ...prev, error: result.error, isGenerating: false }));
      }
    } catch (error) {
      setAIAssistState(prev => ({ ...prev, error: error.message, isGenerating: false }));
    }
  };

  // Generate full content from Kimi AI
  const generateContent = async () => {
    if (!aiAssistState.topic.trim()) {
      setAIAssistState(prev => ({ ...prev, error: 'Please enter a topic' }));
      return;
    }
    setAIAssistState(prev => ({ ...prev, isGenerating: true, error: null }));
    try {
      const { generateBlogContent, parseGeneratedContent, generateHTMLFromContent } = await import('../services/kimiAIService');
      const result = await generateBlogContent(aiAssistState.topic, aiAssistState.category);
      if (result.success) {
        const structured = parseGeneratedContent(result.content);
        const htmlContent = generateHTMLFromContent(structured, previewImage);
        setAIAssistState(prev => ({ ...prev, generatedContent: structured, isGenerating: false }));
        // Auto-fill form with generated content
        setFormData(prev => ({
          ...prev,
          title: structured.title || aiAssistState.topic,
          excerpt: structured.metaDescription || structured.sections[0]?.content?.substring(0, 150) || '',
          content: htmlContent,
          category: aiAssistState.category,
          author: 'Qingshun Team',
          seoTitle: structured.title || aiAssistState.topic,
          seoDescription: structured.metaDescription || ''
        }));
      } else {
        setAIAssistState(prev => ({ ...prev, error: result.error, isGenerating: false }));
      }
    } catch (error) {
      setAIAssistState(prev => ({ ...prev, error: error.message, isGenerating: false }));
    }
  };

  const applyGeneratedContent = () => {
    if (!aiAssistState.generatedContent) return;
    const { generateHTMLFromContent } = require('../services/kimiAIService');
    const htmlContent = generateHTMLFromContent(aiAssistState.generatedContent, previewImage);
    setFormData(prev => ({
      ...prev,
      title: aiAssistState.generatedContent.title || aiAssistState.topic,
      excerpt: aiAssistState.generatedContent.metaDescription || '',
      content: htmlContent,
      category: aiAssistState.category,
      author: 'Qingshun Team',
      seoTitle: aiAssistState.generatedContent.title || aiAssistState.topic,
      seoDescription: aiAssistState.generatedContent.metaDescription || ''
    }));
    setShowAIAssist(false);
    alert('✨ AI-generated content applied to the form! Review and publish when ready.');
  };

  const autoDesign = () => {
    setShowAutoDesignModal(true);
  };

  // Generate beautiful HTML from auto design form data
  const generateAutoDesignContent = (formData) => {
    let content = '';

    // Add main image if provided
    if (formData.mainImagePreview) {
      content += `<img src="${formData.mainImagePreview}" alt="${formData.title}" style="width: 100%; max-width: 800px; height: auto; border-radius: 12px; margin: 20px 0; box-shadow: 0 10px 30px rgba(0,0,0,0.15);" />\n\n`;
    }

    // Add sections
    formData.sections.forEach((section, index) => {
      if (section.heading.trim() || section.content.trim()) {
        // Add heading with emoji
        if (section.heading.trim()) {
          const emojis = ['🚀', '✨', '🎯', '💡', '🔬', '⚡', '🏆', '💼', '🌟', '🎨', '🔥', '⭐'];
          const emoji = emojis[index % emojis.length];
          content += `<h2>${emoji} ${section.heading}</h2>\n`;
        }

        // Add content
        if (section.content.trim()) {
          // Split content by lines and format as paragraphs or lists
          const lines = section.content.split('\n').filter(line => line.trim());
          
          // Check if it looks like a list (lines starting with - or •)
          const isList = lines.some(line => line.trim().match(/^[-•*]/));
          
          if (isList) {
            content += '<ul>\n';
            lines.forEach(line => {
              const cleanLine = line.trim().replace(/^[-•*]\s*/, '');
              if (cleanLine) {
                // Bold the first part if there's a colon
                if (cleanLine.includes(':')) {
                  const [key, ...valueParts] = cleanLine.split(':');
                  const value = valueParts.join(':');
                  content += `  <li><strong>${key.trim()}:</strong>${value}</li>\n`;
                } else {
                  content += `  <li>${cleanLine}</li>\n`;
                }
              }
            });
            content += '</ul>\n\n';
          } else {
            // Format as paragraphs
            lines.forEach(line => {
              if (line.trim()) {
                content += `<p>${line.trim()}</p>\n`;
              }
            });
            content += '\n';
          }
        }

        // Add section image if provided
        if (section.imagePreview) {
          content += `<img src="${section.imagePreview}" alt="${section.heading || 'Section image'}" style="width: 100%; max-width: 800px; height: auto; border-radius: 12px; margin: 20px 0; box-shadow: 0 10px 30px rgba(0,0,0,0.15);" />\n\n`;
        }
      }
    });

    // Add a call-to-action footer
    content += `<p style="background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); color: white; padding: 25px; border-radius: 12px; text-align: center; margin: 30px 0; font-weight: bold; font-size: 18px;">
      Ready to learn more? Contact us today! 📞
    </p>`;

    // Generate excerpt from first section content
    const firstSectionContent = formData.sections[0]?.content || '';
    const excerpt = firstSectionContent.split('\n')[0].substring(0, 150) + '...';

    // Apply to main form
    setFormData(prev => ({
      ...prev,
      title: formData.title,
      excerpt: excerpt || 'Discover more about our products and services.',
      content: content,
      author: 'Qingshun Team',
      category: 'General',
      seoTitle: formData.title,
      seoDescription: excerpt || formData.title
    }));

    // Set main image if provided
    if (formData.mainImagePreview) {
      setPreviewImage(formData.mainImagePreview);
    }

    // Close modal and show success
    setShowAutoDesignModal(false);
    setError('');
    alert('✨ Your beautifully designed blog post is ready!\n\nYou can now review, edit, and publish it.');
  }

  const startNewPost = () => {
    resetForm();
    setShowPostForm(true);
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-gray-900'} pt-32`}>
        <Helmet>
          <title>Admin - Qingshun Electronics</title>
        </Helmet>
        <div className="max-w-4xl mx-auto text-center py-20">
          <h1 className={`text-3xl font-bold mb-6 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
            Loading...
          </h1>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
            Please wait while we verify your credentials
          </p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-gray-900'} pt-32`}>
        <Helmet>
          <title>Admin Login - Qingshun Electronics</title>
          <meta name="description" content="Administrator login for Qingshun Electronics blog management" />
        </Helmet>
        
        <section className={`py-20 px-4 transition-colors duration-300 ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}>
          <div className="max-w-md mx-auto">
            <div className={`rounded-2xl p-8 shadow-lg transition-all duration-300 ${theme === 'dark' ? 'bg-gray-900 border-2 border-gray-800' : 'bg-white border-2 border-gray-300'}`}>
              <h1 className="text-3xl font-bold mb-6 text-center transition-colors duration-300">
                Admin Login
              </h1>
              
              {error && (
                <div className={`mb-6 p-3 rounded-lg transition-colors duration-300 ${theme === 'dark' ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                  {error}
                </div>
              )}
              
              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <label htmlFor="username" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Username
                  </label>
                  <input
                    type="email"
                    id="username"
                    name="username"
                    value={loginData.username}
                    onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                    className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                      theme === 'dark' 
                        ? 'bg-black border-2 border-gray-800 text-white' 
                        : 'bg-white border-2 border-gray-300 text-gray-900'
                    }`}
                    placeholder="admin@qsconn.com"
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label htmlFor="password" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Password
                  </label>
                  <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                    Use your Firebase admin email and password
                  </p>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                      theme === 'dark' 
                        ? 'bg-black border-2 border-gray-800 text-white' 
                        : 'bg-white border-2 border-gray-300 text-gray-900'
                    }`}
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 px-6 py-3 rounded-xl font-semibold text-white hover:from-red-700 hover:to-red-800 transition shadow-lg shadow-red-500/30"
                >
                  Login
                </button>
              </form>
              
              <div className="mt-6 text-center">
                <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Don't have admin access? Contact the site administrator.
                </p>
                <Link 
                  to="/" 
                  className={`inline-flex items-center font-semibold transition-colors duration-300 ${theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-gray-900'} pt-32`}>
      <Helmet>
        <title>Admin Dashboard - Qingshun Electronics</title>
        <meta name="description" content="Administrator dashboard for managing blog posts" />
      </Helmet>
      
      {/* Header */}
      <section className={`py-6 px-4 transition-colors duration-300 ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-3xl font-bold transition-colors duration-300">
            Admin Dashboard
          </h1>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLogout}
              className={`flex items-center px-4 py-2 rounded-lg font-semibold transition-colors duration-300 ${
                theme === 'dark' 
                  ? 'bg-gray-800 text-white hover:bg-gray-700' 
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </section>
      
      <section className={`py-10 px-4 transition-colors duration-300 ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 to-black' : 'bg-gradient-to-br from-gray-100 to-gray-200'}`}>
        <div className="max-w-7xl mx-auto">
          
          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-4 mb-8 border-b pb-4">
            <button
              onClick={() => { setShowMediaManager(false); setShowPictureManager(false); resetForm(); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition-all duration-300 ${
                (!showMediaManager && !showPictureManager)
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-500/30'
                  : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Edit className="w-5 h-5" />
              Blog Posts
            </button>
            <button
              onClick={() => { setShowMediaManager(true); setShowPictureManager(false); loadMedia(); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition-all duration-300 ${
                showMediaManager
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-500/30'
                  : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Image className="w-5 h-5" />
              Site Media Library
            </button>
            <button
              onClick={() => { setShowMediaManager(false); setShowPictureManager(true); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition-all duration-300 ${
                showPictureManager
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-500/30'
                  : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Image className="w-5 h-5" />
              Manage Website Pictures
            </button>
          </div>

          {/* Media Manager */}
          {showMediaManager ? (
            <div className={`rounded-2xl shadow-lg p-8 transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800' : 'bg-white border-2 border-gray-300'}`}>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold transition-colors duration-300">Site Media Library</h2>
                  <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Upload and manage images for use across the entire website
                  </p>
                </div>
              </div>

              {/* Upload Section */}
              <div className={`mb-8 p-6 rounded-2xl ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload New Image
                </h3>
                
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={mediaFileInputRef}
                      onChange={handleMediaUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => mediaFileInputRef.current?.click()}
                      disabled={uploadingFile}
                      className={`w-full py-4 px-6 rounded-xl border-2 border-dashed transition-all duration-300 ${
                        theme === 'dark'
                          ? 'border-gray-700 hover:border-gray-600 text-gray-400'
                          : 'border-gray-300 hover:border-gray-400 text-gray-600'
                      } disabled:opacity-50`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        {uploadingFile ? (
                          <>
                            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8" />
                            <span className="font-medium">Click to select image</span>
                            <span className="text-sm">JPG, PNG, GIF, WebP (Max 10MB)</span>
                          </>
                        )}
                      </div>
                    </button>
                  </div>
                  
                  <div className="md:w-64">
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newImageDescription}
                      onChange={(e) => setNewImageDescription(e.target.value)}
                      className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                        theme === 'dark' 
                          ? 'bg-black border-2 border-gray-800 text-white' 
                          : 'bg-white border-2 border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>

                {mediaError && (
                  <div className={`mt-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                    {mediaError}
                  </div>
                )}
              </div>

              {/* Media Grid */}
              {mediaLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Loading media...</p>
                </div>
              ) : mediaFiles.length === 0 ? (
                <div className="text-center py-16">
                  <FolderOpen className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    No images uploaded yet. Upload your first image above!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {mediaFiles.map((media) => (
                    <div
                      key={media.id}
                      className={`group relative rounded-xl overflow-hidden transition-all duration-300 ${
                        theme === 'dark' ? 'bg-gray-900 border border-gray-800' : 'bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {/* Image Preview */}
                      <div className="relative aspect-square">
                        <img
                          src={media.url}
                          alt={media.description || media.fileName}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://picsum.photos/300/300?blur=2'; }}
                        />
                        
                        {/* Hover Overlay */}
                        <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 ${
                          theme === 'dark' ? 'bg-black/70' : 'bg-black/50'
                        }`}>
                          <button
                            onClick={() => copyImageUrl(media.url)}
                            className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition"
                            title="Copy URL"
                          >
                            {copiedUrl === media.url ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => handleDeleteMedia(media.id, media.storagePath)}
                            className="p-2 rounded-full bg-red-600 hover:bg-red-700 text-white transition"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* File Info */}
                      <div className="p-3">
                        <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {media.description || media.fileName}
                        </p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                          {(media.fileSize / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className={`text-xs mt-6 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                Tip: Click the copy button on any image to copy its URL. Use this URL in any page that needs images.
              </p>
            </div>
          ) : showPictureManager ? (
            <div className={`rounded-2xl shadow-lg p-8 transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800' : 'bg-white border-2 border-gray-300'}`}>
              <div className="mb-6">
                <h2 className="text-2xl font-bold transition-colors duration-300">Manage Website Pictures</h2>
                <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Upload and override the main images across the home, about, and services pages. Default images are used if no custom image is uploaded.
                </p>
              </div>

              {assetError && (
                <div className={`mb-6 p-4 rounded-xl ${theme === 'dark' ? 'bg-red-900/30 text-red-300 border border-red-800' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                  {assetError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {siteAssetsList.map((asset) => {
                  const hasOverride = !!assets[asset.key];
                  const activeUrl = assets[asset.key] || asset.defaultVal;
                  const rawData = rawAssets[asset.key] || {};
                  
                  return (
                    <div 
                      key={asset.key}
                      className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                        theme === 'dark' 
                          ? 'bg-gray-900/50 border-gray-800 hover:border-gray-700' 
                          : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start gap-4 mb-4">
                        <div>
                          <h3 className="text-lg font-bold">{asset.name}</h3>
                          <p className={`text-xs mt-1 leading-relaxed ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{asset.desc}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              hasOverride
                                ? theme === 'dark' ? 'bg-green-950 text-green-400 border border-green-900' : 'bg-green-100 text-green-700 border border-green-200'
                                : theme === 'dark' ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-200 text-gray-600 border border-gray-300'
                            }`}>
                              <span className={`w-1.5 height-1.5 rounded-full ${hasOverride ? 'bg-green-500' : 'bg-gray-400'}`} style={{ width: 6, height: 6, display: 'inline-block' }} />
                              {hasOverride ? 'Custom Override Active' : 'Using Default static image'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Image Preview Container */}
                      <div className="relative rounded-xl overflow-hidden aspect-[16/9] mb-4 border border-gray-700" style={{ position: 'relative', overflow: 'hidden', aspectRatio: '16/9', marginBottom: '16px' }}>
                        <img 
                          src={activeUrl} 
                          alt={asset.name}
                          className="w-full h-full object-cover transition duration-300 hover:scale-105"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.src = 'https://picsum.photos/400/225?blur=2'; }}
                        />
                      </div>

                      {/* Controls */}
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="file"
                          id={`file-input-${asset.key}`}
                          accept="image/*"
                          onChange={(e) => handleAssetUpload(asset.key, e.target.files[0])}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById(`file-input-${asset.key}`).click()}
                          disabled={assetUploadLoading[asset.key]}
                          className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 px-4 py-2 rounded-lg font-semibold text-white transition shadow-lg shadow-red-500/20 disabled:opacity-50"
                        >
                          <Upload className="w-4 h-4" />
                          {assetUploadLoading[asset.key] ? 'Uploading...' : 'Upload New'}
                        </button>
                        
                        {hasOverride && (
                          <button
                            type="button"
                            onClick={() => handleAssetReset(asset.key, rawData.storagePath)}
                            disabled={assetUploadLoading[asset.key]}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold border-2 transition ${
                              theme === 'dark'
                                ? 'border-gray-800 text-gray-400 hover:border-red-900/50 hover:text-red-400 hover:bg-red-950/20'
                                : 'border-gray-200 text-gray-600 hover:border-red-200 hover:text-red-600 hover:bg-red-50'
                            } disabled:opacity-50`}
                          >
                            <Trash2 className="w-4 h-4" />
                            Reset to Default
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : !showPostForm ? (
            <>
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold transition-colors duration-300">
                  Blog Posts
                </h2>
                <button
                  onClick={startNewPost}
                  className="flex items-center bg-gradient-to-r from-red-600 to-red-700 px-4 py-2 rounded-lg font-semibold text-white hover:from-red-700 hover:to-red-800 transition shadow-lg shadow-red-500/30"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </button>
              </div>
              
              <div className={`rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800' : 'bg-white border-2 border-gray-300'}`}>
                <table className="w-full">
                  <thead>
                    <tr className={`border-b transition-colors duration-300 ${theme === 'dark' ? 'border-gray-800' : 'border-gray-300'}`}>
                      <th className="text-left py-4 px-6 font-semibold transition-colors duration-300">Title</th>
                      <th className="text-left py-4 px-6 font-semibold transition-colors duration-300">Author</th>
                      <th className="text-left py-4 px-6 font-semibold transition-colors duration-300">Category</th>
                      <th className="text-left py-4 px-6 font-semibold transition-colors duration-300">Date</th>
                      <th className="text-right py-4 px-6 font-semibold transition-colors duration-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post) => (
                      <tr 
                        key={post.id} 
                        className={`border-b transition-colors duration-300 ${theme === 'dark' ? 'border-gray-800 hover:bg-gray-900/50' : 'border-gray-300 hover:bg-gray-100'}`}
                      >
                        <td className="py-4 px-6 transition-colors duration-300">{post.title}</td>
                        <td className="py-4 px-6 transition-colors duration-300">{post.author}</td>
                        <td className="py-4 px-6 transition-colors duration-300">
                          <span className={`px-2 py-1 rounded-full text-sm transition-colors duration-300 ${
                            theme === 'dark' ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600'
                          }`}>
                            {post.category}
                          </span>
                        </td>
                        <td className="py-4 px-6 transition-colors duration-300">
                          {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <button
                            onClick={() => handleEdit(post)}
                            className={`mr-3 p-2 rounded-lg transition-colors duration-300 ${
                              theme === 'dark' 
                                ? 'text-blue-400 hover:bg-gray-800' 
                                : 'text-blue-600 hover:bg-gray-200'
                            }`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className={`p-2 rounded-lg transition-colors duration-300 ${
                              theme === 'dark' 
                                ? 'text-red-400 hover:bg-gray-800' 
                                : 'text-red-600 hover:bg-gray-200'
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {posts.length === 0 && (
                  <div className="py-12 text-center transition-colors duration-300">
                    <p className={`text-lg transition-colors duration-300 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      No blog posts yet. Create your first post!
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={`rounded-2xl p-8 shadow-lg transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800' : 'bg-white border-2 border-gray-300'}`}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold transition-colors duration-300">
                  {editingPost ? 'Edit Post' : 'Create New Post'}
                </h2>
                <button
                  onClick={resetForm}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-300 ${
                    theme === 'dark' 
                      ? 'bg-gray-800 text-white hover:bg-gray-700' 
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
              </div>
              
              {error && (
                <div className={`mb-6 p-4 rounded-lg transition-colors duration-300 flex items-center gap-2 ${theme === 'dark' ? 'bg-red-900/30 text-red-300 border border-red-800' : 'bg-red-100 text-red-700'}`}>
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              
              {successMessage && (
                <div className={`mb-6 p-4 rounded-lg transition-colors duration-300 flex items-center gap-2 ${theme === 'dark' ? 'bg-green-900/30 text-green-300 border border-green-800' : 'bg-green-100 text-green-700 border border-green-200'}`}>
                  <span>✅</span>
                  <span>{successMessage}</span>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="title" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                        theme === 'dark' 
                          ? 'bg-black border-2 border-gray-800 text-white' 
                          : 'bg-white border-2 border-gray-300 text-gray-900'
                      }`}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="author" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Author *
                    </label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                        theme === 'dark' 
                          ? 'bg-black border-2 border-gray-800 text-white' 
                          : 'bg-white border-2 border-gray-300 text-gray-900'
                      }`}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="excerpt" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Excerpt *
                    </label>
                    <input
                      type="text"
                      id="excerpt"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                        theme === 'dark' 
                          ? 'bg-black border-2 border-gray-800 text-white' 
                          : 'bg-white border-2 border-gray-300 text-gray-900'
                      }`}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="category" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Category *
                    </label>
                    <input
                      type="text"
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                        theme === 'dark' 
                          ? 'bg-black border-2 border-gray-800 text-white' 
                          : 'bg-white border-2 border-gray-300 text-gray-900'
                      }`}
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Featured Image
                    </label>
                    
                    {/* Enhanced Image Upload Area with Drag and Drop */}
                    <div 
                      className={`mb-4 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all duration-300 ${
                        theme === 'dark' 
                          ? 'drag-drop-area-dark' 
                          : 'drag-drop-area'
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add('drag-over');
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove('drag-over');
                      }}
                      onDrop={(e) => {
                        e.currentTarget.classList.remove('drag-over');
                        handleDropImage(e);
                      }}
                    >
                      {previewImage ? (
                        <div className="relative inline-block">
                          <img 
                            src={previewImage} 
                            alt="Preview" 
                            className="max-h-48 rounded-lg mx-auto"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage();
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="py-8">
                          <Upload className={`w-12 h-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                          <p className={`mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            Drag & drop an image here, or click to select
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                            Supports JPG, PNG, GIF (Max 5MB)
                          </p>
                        </div>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageChange}
                        accept="image/*"
                        className="hidden"
                      />
                    </div>
                    
                    {/* Alternative: URL Input */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between">
                        <label htmlFor="image-url" className={`block font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Or enter image URL
                        </label>
                        <button
                          type="button"
                          onClick={() => openMediaManager()}
                          className={`flex items-center gap-1.5 text-sm font-medium transition ${
                            theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                          }`}
                        >
                          <Image className="w-4 h-4" />
                          Browse Media Library
                        </button>
                      </div>
                      <input
                        type="text"
                        id="image-url"
                        name="image"
                        value={formData.image}
                        onChange={handleImageUrlChange}
                        placeholder="https://example.com/image.jpg"
                        className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                          theme === 'dark' 
                            ? 'bg-black border-2 border-gray-800 text-white' 
                            : 'bg-white border-2 border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>
                  
                  {/* SEO Fields */}
                  <div className="md:col-span-2 mt-6">
                    <h3 className="text-xl font-bold mb-4 transition-colors duration-300">SEO Settings</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="seoTitle" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          SEO Title
                        </label>
                        <input
                          type="text"
                          id="seoTitle"
                          name="seoTitle"
                          value={formData.seoTitle}
                          onChange={handleInputChange}
                          placeholder="Leave blank to use post title"
                          className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                            theme === 'dark' 
                              ? 'bg-black border-2 border-gray-800 text-white' 
                              : 'bg-white border-2 border-gray-300 text-gray-900'
                          }`}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="canonicalUrl" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Canonical URL
                        </label>
                        <input
                          type="text"
                          id="canonicalUrl"
                          name="canonicalUrl"
                          value={formData.canonicalUrl}
                          onChange={handleInputChange}
                          placeholder="https://example.com/original-post"
                          className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                            theme === 'dark' 
                              ? 'bg-black border-2 border-gray-800 text-white' 
                              : 'bg-white border-2 border-gray-300 text-gray-900'
                          }`}
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label htmlFor="seoDescription" className={`block mb-2 font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          SEO Description
                        </label>
                        <textarea
                          id="seoDescription"
                          name="seoDescription"
                          value={formData.seoDescription}
                          onChange={handleInputChange}
                          placeholder="Leave blank to use post excerpt"
                          rows={3}
                          className={`w-full rounded-xl py-3 px-4 focus:outline-none focus:border-red-500 transition ${
                            theme === 'dark' 
                              ? 'bg-black border-2 border-gray-800 text-white' 
                              : 'bg-white border-2 border-gray-300 text-gray-900'
                          }`}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className={`block font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Content *
                    </label>
                    
                    {/* AI ASSIST PANEL */}
                    {showAIAssist && (
                      <div className={`mb-6 p-6 rounded-2xl border-2 ${
                        theme === 'dark' ? 'bg-purple-900/20 border-purple-500/50' : 'bg-purple-50 border-purple-300'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold flex items-center gap-2">
                            <Wand2 className="w-5 h-5 text-purple-500" />
                            🤖 Kimi AI Assist
                          </h3>
                          <button
                            onClick={() => setShowAIAssist(false)}
                            className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className={`block mb-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              Topic / Title
                            </label>
                            <input
                              type="text"
                              value={aiAssistState.topic}
                              onChange={(e) => setAIAssistState(prev => ({ ...prev, topic: e.target.value }))}
                              placeholder="e.g., Future of EV Connectors"
                              className={`w-full rounded-xl py-2 px-4 focus:outline-none focus:border-purple-500 transition ${
                                theme === 'dark' ? 'bg-black border border-gray-700 text-white' : 'bg-white border border-gray-300 text-gray-900'
                              }`}
                            />
                          </div>
                          <div>
                            <label className={`block mb-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              Category
                            </label>
                            <select
                              value={aiAssistState.category}
                              onChange={(e) => setAIAssistState(prev => ({ ...prev, category: e.target.value }))}
                              className={`w-full rounded-xl py-2 px-4 focus:outline-none focus:border-purple-500 transition ${
                                theme === 'dark' ? 'bg-black border border-gray-700 text-white' : 'bg-white border border-gray-300 text-gray-900'
                              }`}
                            >
                              <option value="General">General</option>
                              <option value="Automotive">Automotive</option>
                              <option value="Industrial">Industrial</option>
                              <option value="Medical">Medical</option>
                              <option value="Guide">Guide</option>
                              <option value="Technical">Technical</option>
                              <option value="Manufacturing">Manufacturing</option>
                            </select>
                          </div>
                        </div>
                        
                        {aiAssistState.error && (
                          <div className={`mb-4 p-3 rounded-lg ${
                            theme === 'dark' ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                          }`}>
                            ⚠️ {aiAssistState.error}
                          </div>
                        )}
                        
                        {aiAssistState.suggestions.length > 0 && (
                          <div className="mb-4">
                            <h4 className={`text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              💡 Suggested Topics
                            </h4>
                            <div className="space-y-2">
                              {aiAssistState.suggestions.map((idea, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setAIAssistState(prev => ({ ...prev, topic: idea.replace(/^\d+[\.\)]\s*/, '').split('\n')[0].trim(), suggestions: [] }))}
                                  className={`w-full text-left p-3 rounded-lg text-sm ${
                                    theme === 'dark' ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {idea}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={generateIdeas}
                            disabled={aiAssistState.isGenerating}
                            className={`px-4 py-2 rounded-lg font-semibold transition ${
                              theme === 'dark' ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'
                            } disabled:opacity-50`}
                          >
                            💡 Get Ideas
                          </button>
                          <button
                            onClick={generateContent}
                            disabled={aiAssistState.isGenerating || !aiAssistState.topic.trim()}
                            className="px-4 py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition disabled:opacity-50"
                          >
                            {aiAssistState.isGenerating ? '⏳ Generating...' : '✨ Generate Content'}
                          </button>
                          {aiAssistState.generatedContent && (
                            <button
                              onClick={applyGeneratedContent}
                              className="px-4 py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 transition"
                            >
                              ✅ Apply to Form
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* AUTO DESIGN BUTTON - Magic content generation */}
                    <div className="flex flex-wrap items-center gap-3 mb-6">
                      <button
                        type="button"
                        onClick={() => setShowAIAssist(!showAIAssist)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition-all duration-300 ${
                          showAIAssist
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                            : theme === 'dark'
                              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        <Wand2 className="w-5 h-5" />
                        <span>🤖 Kimi AI</span>
                      </button>
                      <button
                        type="button"
                        onClick={autoDesign}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 hover:from-purple-700 hover:via-pink-700 hover:to-red-700 transition-all duration-300 shadow-lg shadow-purple-500/50 hover:shadow-xl hover:shadow-purple-500/70 hover:scale-105 transform"
                        title="Generate a beautiful blog post automatically with professional content, images, and formatting!"
                      >
                        <Wand2 className="w-5 h-5 animate-pulse" />
                        <span>✨ Auto Design</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Visual WYSIWYG Editor - No HTML code visible to user */}
                  <WYSIWYGEditor
                    value={formData.content}
                    onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                    theme={theme}
                    placeholder="Start designing your blog post here. Use the toolbar to format text, add images, create lists, and more!"
                  />
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={togglePreview}
                    className={`px-6 py-3 rounded-xl font-semibold transition-colors duration-300 ${
                      theme === 'dark' 
                        ? 'bg-gray-800 text-white hover:bg-gray-700' 
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                  >
                    {showPreview ? 'Edit' : 'Preview'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className={`px-6 py-3 rounded-xl font-semibold transition-colors duration-300 ${
                      theme === 'dark' 
                        ? 'bg-gray-800 text-white hover:bg-gray-700' 
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saveLoading}
                    className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-3 rounded-xl font-semibold text-white hover:from-red-700 hover:to-red-800 transition shadow-lg shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saveLoading ? (
                      <>
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        {editingPost ? 'Updating...' : 'Publishing...'}
                      </>
                    ) : (
                      <>
                        {editingPost ? '✓ Update Post' : '✓ Publish Post'}
                      </>
                    )}
                  </button>
                </div>
                
                {/* Preview Section */}
                {showPreview && (
                  <div className={`mt-8 rounded-2xl p-6 transition-all duration-300 ${theme === 'dark' ? 'bg-gray-900 border-2 border-gray-800' : 'bg-white border-2 border-gray-300'}`}>
                    <h3 className="text-2xl font-bold mb-4 transition-colors duration-300">Post Preview</h3>
                    
                    {/* Preview Title */}
                    <h1 className="text-3xl font-extrabold mb-2 transition-colors duration-300">{formData.title || 'Post Title'}</h1>
                    
                    {/* Preview Meta */}
                    <div className={`flex flex-wrap items-center text-sm mb-6 transition-colors duration-300 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <span>By {formData.author || 'Author Name'}</span>
                      <span className="mx-2">•</span>
                      <span>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      <span className="mx-2">•</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${theme === 'dark' ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600'}`}>
                        {formData.category || 'Category'}
                      </span>
                    </div>
                    
                    {/* Preview Image */}
                    {previewImage && (
                      <div className="mb-6">
                        <img 
                          src={previewImage} 
                          alt="Preview" 
                          className="w-full h-96 object-cover rounded-xl"
                        />
                      </div>
                    )}
                    
                    {/* Preview Content */}
                    <div 
                      className={`prose max-w-none transition-colors duration-300 ${
                        theme === 'dark' 
                          ? 'text-gray-300 prose-headings:text-white prose-strong:text-white prose-em:text-gray-200' 
                          : 'text-gray-800 prose-headings:text-gray-900 prose-strong:text-gray-900 prose-em:text-gray-700'
                      }`}
                      dangerouslySetInnerHTML={{ __html: formData.content || '<p>Start writing your blog post content here...</p>' }}
                    />
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Auto Design Modal */}
      {showAutoDesignModal && (
        <AutoDesignModal
          theme={theme}
          onGenerate={generateAutoDesignContent}
          onClose={() => setShowAutoDesignModal(false)}
        />
      )}

      {/* Media Library Selection Modal */}
      {selectedImage !== null && showMediaManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className={`w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 ${
            theme === 'dark' ? 'bg-black border-2 border-gray-800' : 'bg-white border-2 border-gray-300'
          }`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-4 border-b ${theme === 'dark' ? 'border-gray-800' : 'border-gray-300'}`}>
              <h3 className="text-xl font-bold">Select Image from Media Library</h3>
              <button
                onClick={() => setSelectedImage(null)}
                className={`p-2 rounded-lg transition ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              {mediaLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Loading images...</p>
                </div>
              ) : mediaFiles.length === 0 ? (
                <div className="text-center py-16">
                  <FolderOpen className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={`text-lg mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    No images in media library yet.
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                    Go to "Site Media Library" tab to upload images first.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mediaFiles.map((media) => (
                    <button
                      key={media.id}
                      onClick={() => selectImageForPost(media.url)}
                      className={`group relative rounded-xl overflow-hidden transition-all duration-300 hover:ring-4 hover:ring-red-500 ${
                        theme === 'dark' ? 'bg-gray-900 border border-gray-800' : 'bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <div className="relative aspect-square">
                        <img
                          src={media.url}
                          alt={media.description || media.fileName}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://picsum.photos/300/300?blur=2'; }}
                        />
                        <div className={`absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 ${
                          theme === 'dark' ? 'bg-black/60' : 'bg-black/40'
                        }`}>
                          <span className="px-3 py-1 rounded-full bg-red-600 text-white text-sm font-medium">
                            Select
                          </span>
                        </div>
                      </div>
                      <div className="p-2">
                        <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {media.description || media.fileName}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;

